import { REFRESH_TOKEN_KEY, TOKEN_KEY } from '@/adapters/auth/storageKeys';
import { isTransientError, TRANSIENT_ERROR_BREADCRUMB } from '@/adapters/graphql/transientError';
import type { Logger } from '@/adapters/logger/types';
import type { SecureStoreAdapter } from '@/adapters/storage/types';

const MISSING_REFRESH_TOKEN_MESSAGE = 'No refresh token available';
const SESSION_EXPIRED_MESSAGE = 'Session expired';

interface MissingRefreshTokenError extends Error {
  readonly isMissingRefreshTokenError: true;
}

interface SessionExpiredError extends Error {
  readonly isSessionExpiredError: true;
}

const createMissingRefreshTokenError = (): MissingRefreshTokenError =>
  Object.assign(new Error(MISSING_REFRESH_TOKEN_MESSAGE), {
    isMissingRefreshTokenError: true,
  } as const);

const createSessionExpiredError = (): SessionExpiredError =>
  Object.assign(new Error(SESSION_EXPIRED_MESSAGE), {
    isSessionExpiredError: true,
  } as const);

const isMissingRefreshTokenError = (error: unknown): error is MissingRefreshTokenError =>
  error instanceof Error && (error as MissingRefreshTokenError).isMissingRefreshTokenError === true;

const isSessionExpiredError = (error: unknown): error is SessionExpiredError =>
  error instanceof Error && (error as SessionExpiredError).isSessionExpiredError === true;

const isExpectedSessionEndError = (
  error: unknown,
): error is MissingRefreshTokenError | SessionExpiredError =>
  isMissingRefreshTokenError(error) || isSessionExpiredError(error);

interface TokenRefreshResult {
  accessToken: string;
  refreshToken: string;
}

interface TokenRefreshAdapters {
  secureStore: SecureStoreAdapter;
  logger: Logger;
  exchangeRefreshToken: (refreshToken: string) => Promise<TokenRefreshResult>;
  onLogout: () => void;
}

interface TokenRefreshManager {
  attemptRefresh: () => Promise<string>;
  handleRetryFailure: () => Promise<void>;
}

const createTokenRefreshManager = (adapters: TokenRefreshAdapters): TokenRefreshManager => {
  const state = { refreshPromise: null as Promise<string> | null };

  const clearTokensAndLogout = async (): Promise<void> => {
    await adapters.secureStore.deleteItem(TOKEN_KEY);
    await adapters.secureStore.deleteItem(REFRESH_TOKEN_KEY);
    adapters.onLogout();
  };

  const executeRefresh = async (): Promise<string> => {
    try {
      adapters.logger.addBreadcrumb('Token Refresh Attempt');
      const refreshToken = await adapters.secureStore.getItem(REFRESH_TOKEN_KEY);

      if (!refreshToken) {
        throw createMissingRefreshTokenError();
      }

      const result = await adapters.exchangeRefreshToken(refreshToken);

      await adapters.secureStore.setItem(TOKEN_KEY, result.accessToken);
      await adapters.secureStore.setItem(REFRESH_TOKEN_KEY, result.refreshToken);

      return result.accessToken;
    } catch (error) {
      if (isExpectedSessionEndError(error)) {
        adapters.logger.addBreadcrumb('Session Ended', { reason: error.message });
        await clearTokensAndLogout();

        throw error;
      }

      if (isTransientError(error)) {
        // The refresh may well succeed on the next attempt, so keep the tokens
        // rather than turning a backend blip into a forced sign-out.
        adapters.logger.addBreadcrumb(TRANSIENT_ERROR_BREADCRUMB, { context: 'executeRefresh' });

        throw error;
      }

      adapters.logger.captureException(error, { context: 'executeRefresh' });
      await clearTokensAndLogout();

      throw error;
    } finally {
      state.refreshPromise = null;
    }
  };

  const attemptRefresh = (): Promise<string> => {
    if (state.refreshPromise) {
      return state.refreshPromise;
    }

    state.refreshPromise = executeRefresh();
    return state.refreshPromise;
  };

  const handleRetryFailure = async (): Promise<void> => {
    await clearTokensAndLogout();
  };

  return { attemptRefresh, handleRetryFailure };
};

export {
  createMissingRefreshTokenError,
  createSessionExpiredError,
  createTokenRefreshManager,
  isExpectedSessionEndError,
  isMissingRefreshTokenError,
  isSessionExpiredError,
};
export type { TokenRefreshAdapters, TokenRefreshManager, TokenRefreshResult };
